// eslint-disable-next-line
import React, { Suspense, lazy, useEffect } from 'react';

import { AuthActions } from 'store/modules/auth';
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from 'react-router-dom';

import { SnackbarType, useSnackbar } from 'components/Snackbar';
import AsyncLoader from 'components/AsyncLoader';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import Modal from 'components/Modal';
import { BusinessName } from 'core/entities';

import { useAppDispatch } from 'store';
import { ConfigActions } from 'store/modules/config';
import { updateServiceWorker } from 'store/modules/service-worker/service-worker.slice';

import { PrivateRoute, Roles, PrivateRouteEntities } from 'core/auth';
import { useTokenVerification } from 'core/auth/hooks';

import { ReactQueryDevtools } from 'react-query-devtools';
import { useManifest, useOnlineStatus } from 'utils/hooks';
import withAllProviders from 'utils/with-all-providers';
import dictionary from 'utils/dictionary';
import {
  OFFLINE_MESSAGE,
  UPDATE_APP_MESSAGE,
  UPDATE_APP_TITLE,
} from './config';
import { EntitiesPage, LoginPage } from './features/Login';
import SessionHandler from './SessionHandler';

const APP_VERSION = process.env.REACT_APP_VERSION;

const MainPage = lazy(() => import('./features/Main'));
const ClaroTvPage = lazy(() => import('./features/ClaroTV/Routes'));
const SearchSDS = lazy(() => import('./features/SearchSds/Routes'));

const WebCoberturaDebugger = lazy(
  () => import('./core/web-cobertura/WebCoberturaDebugger')
);

const App: React.FC = () => {
  const { show, hide } = useSnackbar();
  const dispatch = useAppDispatch();
  const isOnline = useOnlineStatus();
  const tokenVerification = useTokenVerification();
  const {
    newVersionAvailable,
    config,
    request: manifestRequest,
  } = useManifest();

  // load manifest
  useEffect(() => {
    dispatch(ConfigActions.setConfig(config));
  }, [config, dispatch]);

  // logout on new version detected
  useEffect(() => {
    if (newVersionAvailable) {
      dispatch(AuthActions.logout());
    }
  }, [dispatch, newVersionAvailable]);

  // network status
  useEffect(() => {
    if (isOnline) {
      hide();
    } else {
      show({
        type: SnackbarType.error,
        message: OFFLINE_MESSAGE,
      });
    }
  }, [isOnline, show, hide]);

  // global loading handling
  const isLoading =
    manifestRequest.isLoading ||
    (!!manifestRequest.error && manifestRequest.isFetching) ||
    tokenVerification.isLoading;
  const { error } = manifestRequest;

  return (
    <ErrorBoundary>
      <AsyncLoader
        fullScreen
        loading={isLoading}
        error={error}
        onClickRetryButton={() => manifestRequest.refetch()}
        errorMessage={dictionary.app_asyncLoader_errorMessage}
        errorDetail={`v${APP_VERSION}`}
      >
        <Suspense fallback={<Loader absoluteCentered />}>
          <SessionHandler>
            <Router>
              <Switch>
                <Route path="/login" component={LoginPage} />
                <PrivateRoute path="/entities" component={EntitiesPage} />
                <PrivateRoute path="/dashboard" component={MainPage} />
                <PrivateRouteEntities
                  requiredRoles={[
                    Roles.DTHSTH_PY_VENTAS,
                    Roles.DTHSTH_PY_EXCEPCION,
                  ]}
                  path="/claro-tv"
                  redirectPath="/dashboard"
                  requiredBusiness={[BusinessName.ALL, BusinessName.TV]}
                  component={ClaroTvPage}
                />
                <PrivateRoute
                  path="/pdf"
                  redirectPath="/dashboard"
                  component={SearchSDS}
                />
                <PrivateRoute
                  path="/debug-wcf"
                  component={WebCoberturaDebugger}
                />
                <Redirect to="/login" />
              </Switch>
            </Router>
          </SessionHandler>
        </Suspense>
        <Modal
          open={newVersionAvailable}
          title={UPDATE_APP_TITLE}
          okLabel={dictionary.app_modal_ok_label}
          onOk={() => dispatch(updateServiceWorker())}
        >
          {UPDATE_APP_MESSAGE}
        </Modal>
      </AsyncLoader>
      <ReactQueryDevtools />
    </ErrorBoundary>
  );
};

export default withAllProviders(App);
